import { IcpFooter } from "@/components/shared/IcpFooter";
import { PublicHeader } from "@/components/shared/PublicHeader";
import { landingPathFor } from "@/lib/constants/roles";
import { Link, Navigate, createFileRoute } from "@tanstack/react-router";
import {
  FaApple,
  FaArrowRight,
  FaChartLine,
  FaClipboardCheck,
  FaGlobe,
  FaGooglePlay,
  FaLayerGroup,
  FaMobileAlt,
  FaUsers,
} from "react-icons/fa";
import { useAuth } from "../contexts/AuthContext";

const features = [
  {
    icon: FaLayerGroup,
    iconClass: "bg-primary-50 text-primary-600",
    title: "Practice",
    description: "Let students practice and redo questions they got wrong.",
  },
  {
    icon: FaClipboardCheck,
    iconClass: "bg-emerald-50 text-emerald-500",
    title: "Homework",
    description: "Assign homework and let students complete it.",
  },
  {
    icon: FaChartLine,
    iconClass: "bg-rose-50 text-rose-500",
    title: "Track Progress",
    description: "See scores and per-question results as soon as students finish.",
  },
  {
    icon: FaUsers,
    iconClass: "bg-sky-50 text-sky-500",
    title: "Engage & Motivate",
    description: "Fun, game-like experience keeps kids coming back.",
  },
];

const heroButton = "rounded-full px-8 py-3.5 text-base font-semibold transition shadow-sm";

function HomePage() {
  return (
    <div className="flex flex-col min-h-screen bg-white">
      <PublicHeader activePage="home" />

      <main className="flex-1">
        <section className="relative overflow-hidden bg-gradient-to-b from-amber-50 to-amber-50/30">
          {/* Soft colour blobs behind the illustration */}
          <div className="pointer-events-none absolute -right-20 top-10 h-80 w-80 rounded-full bg-amber-200/50 blur-3xl" />
          <div className="pointer-events-none absolute right-40 bottom-0 h-72 w-72 rounded-full bg-secondary-100/60 blur-3xl" />

          <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-6 py-16 lg:grid-cols-2 lg:py-24">
            <div>
              <span className="inline-flex items-center rounded-full bg-primary-50 px-4 py-1.5 label text-primary-700 ring-1 ring-primary-100">
                The English Learning App for Young Learners
              </span>

              <h1 className="mt-6 text-5xl font-extrabold leading-tight tracking-tight sm:text-6xl">
                <span className="text-secondary-800">Practice Today,</span>
                <br />
                <span className="text-accent-500">Speak </span>
                <span className="relative inline-block text-rose-500">
                  Tomorrow!
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 200 10"
                    preserveAspectRatio="none"
                    className="absolute -bottom-1 left-0 h-2.5 w-full text-amber-300"
                  >
                    <path
                      d="M2 8C50 2 150 2 198 5"
                      stroke="currentColor"
                      strokeWidth="5"
                      strokeLinecap="round"
                      fill="none"
                    />
                  </svg>
                </span>
              </h1>

              <p className="mt-8 max-w-md body-lg text-gray-600">
                Fun, interactive lessons and personalized practice that help kids build real English
                skills — step by step.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Link
                  to="/login"
                  className={`${heroButton} ring-1 ring-gray-200 bg-primary-600 text-white hover:bg-primary-700`}
                >
                  Sign In
                </Link>
              </div>

              <Link
                to="/download"
                className="mt-8 inline-flex items-center gap-3 body-sm text-gray-500 hover:text-gray-700"
              >
                <FaMobileAlt className="h-4 w-4" />
                Available on mobile, tablet and web
                <span className="h-5 w-px bg-gray-300" />
                <span className="flex items-center gap-3 text-gray-600">
                  <FaApple className="h-5 w-5" />
                  <FaGooglePlay className="h-4 w-4" />
                  <FaGlobe className="h-4 w-4" />
                </span>
              </Link>
            </div>

            <img
              src="/images/landing/hero.png"
              alt="The Pocket Teacher app running on a phone"
              className="mx-auto w-full max-w-[300px] drop-shadow-2xl"
            />
          </div>
        </section>

        <section className="bg-primary-50/40 px-6 py-16">
          <div className="mx-auto grid max-w-7xl gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {features.map(({ icon: Icon, iconClass, title, description }) => (
              <Link
                key={title}
                to="/login"
                className="group flex items-center gap-4 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-100 transition hover:shadow-md"
              >
                <span
                  className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl ${iconClass}`}
                >
                  <Icon className="h-6 w-6" />
                </span>
                <div className="min-w-0 flex-1">
                  <h2 className="card-title text-gray-900">{title}</h2>
                  <p className="mt-1 body-sm text-gray-500">{description}</p>
                </div>
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-50 text-gray-400 transition group-hover:bg-primary-50 group-hover:text-primary-600">
                  <FaArrowRight className="h-3 w-3" />
                </span>
              </Link>
            ))}
          </div>
        </section>
      </main>

      <IcpFooter />
    </div>
  );
}

/** Signed-in staff never need the marketing page; it still shows while the session resolves. */
function HomeRoute() {
  const { user, isAuthenticated, isPending } = useAuth();
  const landingPath = landingPathFor(user?.role);

  if (!isPending && isAuthenticated && landingPath) {
    return <Navigate to={landingPath} replace />;
  }

  return <HomePage />;
}

export const Route = createFileRoute("/")({
  component: HomeRoute,
});
